import * as THREE from 'three';
import { proxied } from './assets';

// loadTexture — one shared, LEARNING texture loader.
//
// Assets come back as SIGNED urls from protected GCS buckets. Some buckets send
// CORS headers our origin can use (load them DIRECTLY — fastest, no server hop);
// others don't, and must go through the API asset proxy. We can't know which
// without trying.
//
// Naively "try direct, fall back to proxy" makes EVERY image pay a failed request
// first — that's what made a 6-cover billboard grid crawl. So the outcome is
// remembered PER HOST: the first image discovers the working route and every
// later one goes straight there. Also de-dupes in-flight loads of the same URL,
// so the same cover on two surfaces is fetched once.
// The learning has to cover loads that are ALREADY IN FLIGHT, not just later
// ones. On first entry the portals, plaques and placements all start together,
// and every one of them began before the first had finished discovering the
// route — so each independently tried direct, and each independently failed.
// One wasted round-trip per texture, and a wall of CORS errors in the console
// that reads like a broken build.
//
// So the first load for a host becomes the PROBE and publishes its finding;
// everyone else waits for it and goes straight to the working route. Exactly one
// direct attempt per host per session, whatever the concurrency.
const hostMode = new Map();   // host -> 'direct' | 'proxy'
const hostProbe = new Map();  // host -> Promise<'direct' | 'proxy'> while probing
const cache = new Map();      // url -> Promise<Texture>

const loader = new THREE.TextureLoader();
loader.setCrossOrigin('anonymous');

function hostOf(url) {
  try { return new URL(url, window.location.href).host; } catch { return 'unknown'; }
}

function loadOne(src) {
  return new Promise((resolve, reject) => {
    loader.load(src, (t) => { t.colorSpace = THREE.SRGBColorSpace; t.anisotropy = 4; resolve(t); }, undefined, reject);
  });
}

/** Load a texture, learning the right route per host. Resolves null on failure. */
export function loadTexture(url) {
  if (!url) return Promise.resolve(null);
  if (cache.has(url)) return cache.get(url);

  const host = hostOf(url);
  const viaProxy = proxied(url);

  const p = (async () => {
    // Already know the route, or a probe is running? Then never guess: take the
    // known route, or wait for the one load that is finding out.
    let mode = hostMode.get(host);
    if (!mode && hostProbe.has(host)) mode = await hostProbe.get(host);
    if (mode) {
      const src = (mode === 'proxy' && viaProxy !== url) ? viaProxy : url;
      try { return await loadOne(src); } catch { console.warn('[texture] failed:', url); return null; }
    }

    // This load is the probe for its host — it discovers the route by being the
    // one that pays for a wrong guess, and publishes the answer either way.
    let settle;
    hostProbe.set(host, new Promise((r) => { settle = r; }));
    const finish = (m) => { hostMode.set(host, m); hostProbe.delete(host); settle(m); };
    try {
      const t = await loadOne(url);
      finish('direct');
      return t;
    } catch {
      if (viaProxy === url) {
        finish('direct');            // no proxy route exists; do not strand waiters
        console.warn('[texture] failed (no proxy route):', url);
        return null;
      }
      try {
        const t = await loadOne(viaProxy);
        finish('proxy');
        return t;
      } catch {
        finish('proxy');             // still publish, so waiters do not re-probe
        console.warn('[texture] failed (direct + proxy):', url);
        return null;
      }
    }
  })();

  cache.set(url, p);
  return p;
}
